// =============================================================================
// LegalPage.js, Shared layout for /terms, /privacy, /refunds.
//
// Calm 2120 styling: max-width prose container, soft hero, top nav consistent
// with the public Pricing page so signed-out visitors don't lose context.
//
// Content blocks are passed as children; the parent page supplies the
// heading + last-updated date + sections array.
// =============================================================================
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

export default function LegalPage({ title, subtitle, lastUpdated, sections = [], children, showPreIncorpNotice = true }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <div className="sticky top-0 z-30 backdrop-blur bg-background/70 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5" data-testid="legal-logo-link">
            <img src="/brand/logo-mark.png" alt="2120" className="h-8 w-8" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-5 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-5 w-auto hidden dark:block" />
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard"><Button variant="ghost" className="rounded-xl">Back to app</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="rounded-xl">Sign in</Button></Link>
                <Link to="/signup"><Button className="rounded-xl">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-hero-soft border-b border-border/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="legal-title">
            {title}
          </h1>
          {subtitle && (
            <p className="text-muted-foreground mt-3 leading-relaxed max-w-2xl">{subtitle}</p>
          )}
          {lastUpdated && (
            <p className="text-xs text-muted-foreground mt-4 uppercase tracking-[0.18em]">
              Last updated · {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 prose prose-neutral dark:prose-invert max-w-none">
        {showPreIncorpNotice && (
          <aside
            className="not-prose mb-10 rounded-2xl border border-border/60 bg-muted/30 px-4 sm:px-5 py-4 text-xs sm:text-sm leading-relaxed text-muted-foreground"
            data-testid="legal-pre-incorporation-notice"
          >
            <strong className="text-foreground font-medium">Note on entity status.</strong>{' '}
            2120 is currently in private beta and is operated by its founding team in Bangalore, India.
            A formal legal entity will be announced before commercial general availability. For any privacy,
            legal, billing, or product matters, please reach us at{' '}
            <a
              href="mailto:privacy@2120.money"
              className="text-foreground underline underline-offset-2 hover:no-underline"
            >
              privacy@2120.money
            </a>
            .
          </aside>
        )}
        {children}
        {sections.map((s, i) => (
          <section key={i} className="mb-10" data-testid={`legal-section-${i}`}>
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-8 mb-3">
              {s.heading}
            </h2>
            {s.body.split('\n\n').map((para, j) => (
              <p key={j} className="text-sm sm:text-[15px] text-foreground/85 leading-relaxed mb-3">
                {para}
              </p>
            ))}
            {s.list && (
              <ul className="mt-2 space-y-1.5 text-sm sm:text-[15px] text-foreground/85 leading-relaxed list-disc pl-5 marker:text-primary">
                {s.list.map((item, k) => (
                  <li key={k}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      {/* Footer with legal links */}
      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <img src="/brand/logo-mark.png" alt="" className="h-5 w-5" />
            <span>2120 · Calmer money clarity</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
            <Link to="/terms"           className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy"         className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/cookies"         className="hover:text-foreground transition-colors" data-testid="footer-cookies-link">Cookies</Link>
            <Link to="/acceptable-use"  className="hover:text-foreground transition-colors" data-testid="footer-aup-link">Acceptable use</Link>
            <Link to="/refunds"         className="hover:text-foreground transition-colors" data-testid="footer-refunds-link">Refunds</Link>
            <Link to="/pricing"         className="hover:text-foreground transition-colors" data-testid="footer-pricing-link">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
